import React from 'react';
import AnimatedSection from '../components/AnimatedSection';
import SectionHeading from '../components/SectionHeading';
import SEO from '../components/SEO';

function PrivacyPolicyPage() {
  const effectiveDate = 'October 2, 2026';

  const sections: { heading: string; body: React.ReactNode }[] = [
    {
      heading: '1. Introduction and Scope',
      body: (
        <div className="space-y-3">
          <p>
            Impresstars (&ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;) is a business consulting, technology,
            and creative services company based in London, Ontario, Canada. We respect your privacy and are committed
            to protecting your personal information in accordance with applicable Canadian privacy laws, including
            the Personal Information Protection and Electronic Documents Act (PIPEDA) and the Ontario Personal
            Information Protection Act, where applicable.
          </p>
          <p>
            This Privacy Policy explains what personal information we collect, why we collect it, how we use and
            share it, how long we keep it, and the choices and rights you have regarding your information. It applies
            to information collected through our website at impresstars.com (the &ldquo;Site&rdquo;), through our
            contact forms, and during the course of providing our services (the &ldquo;Services&rdquo;).
          </p>
          <p>
            This Privacy Policy does not apply to third-party websites or services that we may link to or recommend.
            We encourage you to review the privacy policies of any third-party services you use.
          </p>
        </div>
      ),
    },
    {
      heading: '2. Information We Collect',
      body: (
        <div className="space-y-4">
          <p><strong>3.1 Information you provide directly.</strong> When you submit a contact form, request a
          proposal, or engage our Services, we may collect:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Your name and job title;</li>
            <li>Your email address and phone number;</li>
            <li>Your company name, industry, and website;</li>
            <li>Details about your project, business needs, or service inquiry;</li>
            <li>Any other information you choose to include in your message or communications;</li>
            <li>Billing information such as company address and tax identifiers (we do not store full credit card numbers — payments are processed by third-party payment processors).</li>
          </ul>
          <p><strong>3.2 Technical and usage information.</strong> When you visit the Site, we or our service
          providers may automatically collect:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>IP address and approximate geographic location (city/region level);</li>
            <li>Browser type, operating system, and device information;</li>
            <li>Pages visited, time spent on pages, and referring URLs;</li>
            <li>Information collected through cookies and similar technologies (see Section 8 below).</li>
          </ul>
          <p><strong>3.3 Project-related information.</strong> During the course of an active engagement, we may
          receive or access business documents, customer data, marketing materials, login credentials, and other
          information necessary to deliver the Services. We handle this information in accordance with the terms of
          the applicable Engagement Agreement and our confidentiality obligations (see Section 9 of our Terms
          &amp; Conditions).</p>
        </div>
      ),
    },
    {
      heading: '3. How We Use Your Information',
      body: (
        <div className="space-y-3">
          <p>We use the personal information we collect for the following purposes:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Responding to inquiries:</strong> to answer your questions, provide quotes, and discuss potential projects;</li>
            <li><strong>Delivering Services:</strong> to execute the work outlined in your Engagement Agreement, including creating deliverables and providing support;</li>
            <li><strong>Communication:</strong> to send project updates, request feedback, provide support, and communicate about deliverables;</li>
            <li><strong>Billing:</strong> to issue invoices, process payments, and maintain financial records;</li>
            <li><strong>Improvement:</strong> to analyze website usage, improve our Site and Services, and develop new offerings;</li>
            <li><strong>Security and fraud prevention:</strong> to detect, investigate, and prevent fraud, misuse, or security incidents;</li>
            <li><strong>Legal compliance:</strong> to meet our obligations under applicable laws and respond to lawful requests from authorities;</li>
            <li><strong>Marketing:</strong> to send occasional updates, newsletters, or service announcements. You may unsubscribe at any time using the link in any marketing email or by contacting us directly.</li>
          </ul>
          <p>
            We do not use your personal information for automated decision-making or profiling that produces legal or
            similarly significant effects.
          </p>
        </div>
      ),
    },
    {
      heading: '4. Legal Basis for Processing',
      body: (
        <div className="space-y-3">
          <p>
            Under Canadian privacy law, we collect, use, and disclose personal information only with your consent or
            as otherwise permitted or required by law. By submitting your information through the Site or engaging
            our Services, you consent to the collection, use, and disclosure described in this Privacy Policy.
          </p>
          <p>
            For individuals located in jurisdictions subject to the General Data Protection Regulation (GDPR), our
            legal bases for processing are:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Consent:</strong> when you submit a contact form or sign up for communications;</li>
            <li><strong>Contract performance:</strong> when processing is necessary to deliver Services under an Engagement Agreement;</li>
            <li><strong>Legitimate interests:</strong> for website analytics, security, and business improvement, balanced against your privacy rights;</li>
            <li><strong>Legal obligation:</strong> when we are required to retain or disclose information by law.</li>
          </ul>
          <p>
            You may withdraw your consent at any time by contacting us at info@impresstars.com. Withdrawing consent
            does not affect the lawfulness of processing that occurred before withdrawal.
          </p>
        </div>
      ),
    },
    {
      heading: '5. How We Share Your Information',
      body: (
        <div className="space-y-3">
          <p>
            We do not sell your personal information. We may share your information with the following categories of
            recipients, only to the extent necessary for the purposes described above:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Service providers:</strong> hosting platforms, analytics tools, email and communication services, CRM systems, payment processors, and project management tools that support our operations;</li>
            <li><strong>Contractors and partners:</strong> independent professionals we engage to help deliver specific projects, who are bound by confidentiality obligations;</li>
            <li><strong>Legal and financial advisors:</strong> accountants, auditors, or lawyers engaged by us, subject to professional secrecy obligations;</li>
            <li><strong>Legal authorities:</strong> where required by law, court order, or government regulation, or to protect our rights, safety, or property.</li>
          </ul>
          <p>
            Our third-party service providers process information on our behalf and under our instructions. They are
            not permitted to use your information for their own purposes. We select providers that demonstrate
            appropriate data protection practices.
          </p>
        </div>
      ),
    },
    {
      heading: '6. International Data Transfers',
      body: (
        <div className="space-y-3">
          <p>
            Impresstars is based in Ontario, Canada. However, some of our service providers (such as cloud hosting,
            analytics, and email platforms) may process or store data in the United States or other countries. When
            your information is transferred outside Canada, it may be subject to the laws of the receiving country,
            which may differ from Canadian privacy law.
          </p>
          <p>
            We take reasonable steps to ensure that cross-border transfers are protected through contracts with our
            service providers and by selecting providers with appropriate security and privacy certifications. For
            transfers from the EU/EEA, we rely on appropriate safeguards such as Standard Contractual Clauses where
            required.
          </p>
        </div>
      ),
    },
    {
      heading: '7. Cookies and Tracking Technologies',
      body: (
        <div className="space-y-3">
          <p>
            We use cookies and similar technologies (such as web beacons and pixels) to operate and improve the Site,
            analyze traffic, and enhance your experience. The types of cookies we use include:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Essential cookies:</strong> required for the Site to function correctly (e.g., remembering form inputs). These cannot be disabled.</li>
            <li><strong>Analytics cookies:</strong> help us understand how visitors use the Site so we can improve it. These may be set by us or by third-party analytics providers.</li>
            <li><strong>Preference cookies:</strong> remember your settings and choices (e.g., language or display preferences).</li>
            <li><strong>Marketing cookies:</strong> may be used to measure the effectiveness of advertising campaigns, if applicable.</li>
          </ul>
          <p>
            You can manage or disable cookies through your browser settings. Most browsers allow you to refuse or
            delete cookies. Disabling some cookies may affect Site functionality. We do not use cookies to collect
            sensitive personal information such as health or financial data.
          </p>
        </div>
      ),
    },
    {
      heading: '8. Data Retention',
      body: (
        <div className="space-y-3">
          <p>
            We retain personal information only as long as necessary to fulfill the purposes for which it was
            collected, including to comply with legal, accounting, or reporting requirements. General retention
            guidelines:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Inquiry data:</strong> information submitted through contact forms is retained for up to 24 months after the last interaction, unless you become a client;</li>
            <li><strong>Client records:</strong> project files, contracts, and billing records are retained for up to 7 years after the engagement ends, as required for tax and legal purposes;</li>
            <li><strong>Marketing data:</strong> email subscribers&rsquo; data is retained until you unsubscribe or request deletion;</li>
            <li><strong>Technical/usage data:</strong> retained for up to 26 months, after which it is aggregated or deleted.</li>
          </ul>
          <p>
            When information is no longer needed, we securely delete or anonymize it. Anonymized data that can no
            longer identify you may be retained indefinitely for statistical analysis.
          </p>
        </div>
      ),
    },
    {
      heading: '9. Security Measures',
      body: (
        <div className="space-y-3">
          <p>
            We implement reasonable administrative, technical, and organizational safeguards to protect personal
            information against unauthorized access, loss, theft, misuse, or alteration. These measures include:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Encryption of data in transit using TLS/SSL and at rest where supported by our platforms;</li>
            <li>Access controls limiting personal information to authorized personnel who need it for their role;</li>
            <li>Regular security reviews of our systems and service providers;</li>
            <li>Secure disposal of physical and electronic records when no longer needed;</li>
            <li>Employee and contractor training on privacy and data handling practices.</li>
          </ul>
          <p>
            No method of transmission or storage is completely secure. While we strive to protect your information,
            we cannot guarantee absolute security. If a data breach occurs that poses a real risk of significant
            harm, we will notify affected individuals and authorities as required by applicable law.
          </p>
        </div>
      ),
    },
    {
      heading: '10. Your Privacy Rights',
      body: (
        <div className="space-y-3">
          <p>
            Subject to applicable law, you have the following rights regarding your personal information:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Access:</strong> request a copy of the personal information we hold about you;</li>
            <li><strong>Correction:</strong> request that we correct inaccurate or incomplete information;</li>
            <li><strong>Deletion:</strong> request that we delete your personal information, subject to legal retention obligations;</li>
            <li><strong>Withdrawal of consent:</strong> withdraw your consent to processing at any time, subject to contractual or legal restrictions;</li>
            <li><strong>Objection:</strong> object to certain types of processing, such as direct marketing;</li>
            <li><strong>Portability:</strong> request your personal information in a structured, machine-readable format, where applicable;</li>
            <li><strong>Restriction:</strong> request that we restrict processing in certain circumstances (e.g., while a correction request is being verified).</li>
          </ul>
          <p>
            To exercise any of these rights, contact us at info@impresstars.com with &ldquo;Privacy Request&rdquo; in
            the subject line. We will respond within 30 days. We may need to verify your identity before processing
            your request and may refuse or charge a reasonable fee for requests that are manifestly unfounded or
            excessive, as permitted by law.
          </p>
        </div>
      ),
    },
    {
      heading: '11. Children\u2019s Privacy',
      body: (
        <p>
          Our Services are intended for businesses and adults. We do not knowingly collect personal information from
          children under 16 through the Site. If you believe we have inadvertently collected information from a
          child, please contact us and we will take steps to delete it promptly.
        </p>
      ),
    },
    {
      heading: '12. Third-Party Links and Referrals',
      body: (
        <div className="space-y-3">
          <p>
            The Site contains links to third-party websites and tools, some of which are referral or affiliate links.
            When you click a link and visit a third-party site, you are subject to that site&rsquo;s privacy policy,
            not ours. We are not responsible for how third parties collect, use, or protect your information. We
            encourage you to review their privacy policies before providing any personal information.
          </p>
          <p>
            Our referral relationships do not give us access to your account or data on those third-party platforms.
            Any information you provide directly to a third-party provider is governed by your relationship with that
            provider.
          </p>
        </div>
      ),
    },
    {
      heading: '13. Changes to This Privacy Policy',
      body: (
        <div className="space-y-3">
          <p>
            We may revise this Privacy Policy periodically to reflect changes in our practices, legal requirements,
            or business operations. When we make material changes, we will update the &ldquo;Effective date&rdquo; at
            the top of this page and, where appropriate, notify clients with active engagements by email.
          </p>
          <p>
            We encourage you to review this page periodically. Your continued use of the Site or Services after a
            revised Privacy Policy takes effect constitutes your acceptance of the updated policy.
          </p>
        </div>
      ),
    },
    {
      heading: '14. Contact / Privacy Requests',
      body: (
        <div className="space-y-3">
          <p>
            If you have questions about this Privacy Policy or wish to exercise any of your privacy rights, please
            contact us:
          </p>
          <ul className="list-none space-y-2">
            <li><span className="text-gray-400">Email:</span> <a href="mailto:info@impresstars.com" className="text-[#FDCB12] hover:text-[#FF5500] transition">info@impresstars.com</a></li>
            <li><span className="text-gray-400">Website:</span> impresstars.com</li>
            <li><span className="text-gray-400">Location:</span> London, Ontario, Canada</li>
          </ul>
          <p className="text-sm text-gray-500 pt-2">
            Please include &ldquo;Privacy Request&rdquo; in the subject line of your email so we can route it
            promptly. If you are a resident of the EU or UK and wish to lodge a complaint with a supervisory
            authority, you may do so in the jurisdiction where you live or work, or where the alleged infringement
            occurred.
          </p>
        </div>
      ),
    },
  ];

  return (
    <>
      <SEO
        title="Privacy Policy"
        description="Privacy Policy describing how Impresstars collects, uses, shares, and protects personal information in accordance with Canadian privacy law."
        canonicalPath="/privacy-policy"
      />
      <main className="container mx-auto px-4 py-12 text-white">
        <SectionHeading eyebrow="Legal" title="Privacy Policy" />
        <AnimatedSection animation="fade-in-up" delay={100}>
          <p className="text-zinc-300 mb-8 text-center">Effective date: {effectiveDate}</p>
        </AnimatedSection>

        <div className="max-w-3xl mx-auto space-y-6">
          {sections.map((section, index) => (
            <AnimatedSection key={section.heading} animation="fade-in-up" delay={((index % 5) + 1) * 100 as 100 | 200 | 300 | 400 | 500}>
              <section className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6">
                <h2 className="text-2xl font-semibold text-white mb-3">{section.heading}</h2>
                <div className="text-zinc-200 leading-relaxed space-y-2">{section.body}</div>
              </section>
            </AnimatedSection>
          ))}

          <AnimatedSection animation="fade-in-up" delay={200}>
            <p className="text-sm text-gray-500 text-center pt-4">
              This document was last updated on {effectiveDate}.
            </p>
          </AnimatedSection>
        </div>
      </main>
    </>
  );
}

export default PrivacyPolicyPage;
